import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  BarChart3, RefreshCw, Loader2, Sparkles, Search, Plus, Users, Trash2, Eye
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { Instagram, Youtube } from "lucide-react";

const MIX_COLORS = { reels: "#c6a96f", carousels: "#5ba89d", text_posts: "#5b8ec4", photos: "#c47272" };
const MIX_LABELS = { reels: "Reels", carousels: "Carousels", text_posts: "Text", photos: "Photos" };

function StrategyCard({ strategy, brand, onCalibrate, onDiscover }) {
  const [calibrating, setCalibrating] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const mix = strategy.content_mix || {};
  const pieData = Object.entries(mix).map(([key, val]) => ({ name: MIX_LABELS[key] || key, value: val, color: MIX_COLORS[key] || "#71717a" }));
  const tooltipStyle = { contentStyle: { background: "#1a1d21", border: "1px solid #2a2e36", borderRadius: "12px", fontSize: "12px", color: "#d4d4d8" } };

  const handleCalibrate = async () => {
    setCalibrating(true);
    await onCalibrate(strategy.brand_id);
    setCalibrating(false);
  };

  const handleDiscover = async () => {
    setDiscovering(true);
    await onDiscover(strategy.brand_id);
    setDiscovering(false);
  };

  return (
    <div className="bg-studio-raised border border-[#2a2e36] rounded-xl overflow-hidden" data-testid={`strategy-${strategy.brand_id}`}>
      <div className="flex items-center gap-3 p-4 border-b border-[#222630]">
        <div className="w-3 h-3 rounded-full" style={{ background: brand?.color_hex || "#71717a" }} />
        <h3 className="text-sm font-semibold text-zinc-100">{brand?.name || strategy.brand_id}</h3>
        <span className="text-xs text-zinc-500 font-mono ml-auto">{strategy.posts_per_week || 0}x/week</span>
      </div>

      <div className="p-4 grid grid-cols-2 gap-4">
        {/* Content Mix Pie */}
        <div>
          <p className="section-label mb-2">// content mix</p>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={35} outerRadius={60} dataKey="value" stroke="none">
                {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip {...tooltipStyle} formatter={(v) => `${v}%`} />
              <Legend formatter={(v) => <span style={{ color: "#a1a1aa", fontSize: "11px" }}>{v}</span>} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Pillars + Sources */}
        <div className="space-y-3">
          <div>
            <p className="section-label mb-1.5">// content pillars</p>
            <div className="flex flex-wrap gap-1">
              {(strategy.content_pillars || []).map((p) => (
                <span key={p} className="text-[10px] px-2 py-0.5 rounded-full border border-[#2a2e36] text-zinc-400">{p}</span>
              ))}
            </div>
          </div>
          <div>
            <p className="section-label mb-1.5">// sources</p>
            <div className="flex flex-wrap gap-1">
              {(strategy.source_preferences || []).map((s) => (
                <span key={s} className="text-[10px] px-2 py-0.5 rounded-full border border-[#2a2e36] text-zinc-300 capitalize">{s.replace("_", " ")}</span>
              ))}
            </div>
          </div>
          {strategy.rules?.length > 0 && (
            <div>
              <p className="section-label mb-1.5">// rules</p>
              {strategy.rules.map((r, i) => (
                <p key={i} className="text-[11px] text-zinc-400">- {r}</p>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Reasoning */}
      {strategy.reasoning && (
        <div className="px-4 pb-3">
          <p className="section-label mb-1">// reasoning</p>
          <p className="text-xs text-zinc-400 leading-relaxed">{strategy.reasoning}</p>
          {strategy.last_calibrated && (
            <p className="text-[10px] text-zinc-600 font-mono mt-1">calibrated: {new Date(strategy.last_calibrated).toLocaleDateString()}</p>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 p-3 border-t border-[#222630]">
        <button onClick={handleCalibrate} disabled={calibrating}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gold/30 text-gold text-xs hover:bg-gold/5 transition-colors disabled:opacity-40"
          data-testid={`calibrate-${strategy.brand_id}`}>
          {calibrating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
          {calibrating ? "Calibrating..." : "Calibrate Strategy"}
        </button>
        <button onClick={handleDiscover} disabled={discovering}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#363b45] text-zinc-400 text-xs hover:text-teal hover:border-teal/30 transition-colors disabled:opacity-40"
          data-testid={`discover-${strategy.brand_id}`}>
          {discovering ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
          Find Competitors
        </button>
      </div>
    </div>
  );
}

export default function Strategy() {
  const [strategies, setStrategies] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState(null);
  const [addingComp, setAddingComp] = useState(null);

  const fetchData = async () => {
    try {
      const [stratRes, brandRes] = await Promise.all([
        api.get("/strategy"),
        api.get("/brands"),
      ]);
      setStrategies(stratRes.data.strategies);
      setBrands(brandRes.data.brands);
    } catch { toast.error("Failed to load strategies"); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const brandMap = {};
  brands.forEach((b) => { brandMap[b.id] = b; });

  const handleCalibrate = async (brandId) => {
    try {
      const res = await api.post(`/strategy/${brandId}/calibrate`);
      toast.success(`Strategy calibrated for ${brandMap[brandId]?.name || brandId}`);
      fetchData();
    } catch { toast.error("Calibration failed"); }
  };

  const handleDiscover = async (brandId) => {
    try {
      const res = await api.post(`/competitors/discover/${brandId}`);
      setSuggestions({ brandId, list: res.data.suggestions });
      toast.success(`Found ${res.data.suggestions.length} competitor suggestions`);
    } catch { toast.error("Discovery failed"); }
  };

  const handleAddCompetitor = async (suggestion, brandId) => {
    setAddingComp(suggestion.handle);
    try {
      await api.post("/competitors", {
        name: suggestion.name,
        handle: suggestion.handle,
        platform: suggestion.platform || "instagram",
        brand_ids: [brandId],
      });
      toast.success(`Added ${suggestion.name}`);
      setSuggestions((prev) => prev ? { ...prev, list: prev.list.filter((s) => s.handle !== suggestion.handle) } : null);
    } catch { toast.error("Failed to add"); }
    setAddingComp(null);
  };

  if (loading) return <div className="flex items-center justify-center h-40"><div className="spinner-gold" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="strategy-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-zinc-100">Content Strategy</h1>
        <p className="section-label mt-2">// per-brand strategy -- tell jarvis to adjust anytime</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {strategies.map((s) => (
          <StrategyCard key={s.brand_id} strategy={s} brand={brandMap[s.brand_id]}
            onCalibrate={handleCalibrate} onDiscover={handleDiscover} />
        ))}
      </div>

      {/* Competitor suggestions panel */}
      {suggestions && suggestions.list.length > 0 && (
        <div className="bg-studio-raised border border-teal/30 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Users size={16} className="text-teal" />
            <h3 className="text-sm font-semibold text-zinc-200">Competitor Suggestions for {brandMap[suggestions.brandId]?.name}</h3>
            <button onClick={() => setSuggestions(null)} className="ml-auto text-xs text-zinc-500 hover:text-zinc-300">Dismiss</button>
          </div>
          <div className="space-y-2">
            {suggestions.list.map((s) => (
              <div key={s.handle} className="flex items-center gap-3 p-3 bg-studio-surface rounded-lg">
                <div className="flex-1">
                  <p className="text-sm text-zinc-200 font-medium">{s.name}</p>
                  <p className="text-xs text-zinc-500 font-mono">{s.handle} ({s.platform || "instagram"})</p>
                  {s.reason && <p className="text-xs text-zinc-400 mt-1">{s.reason}</p>}
                </div>
                <button onClick={() => handleAddCompetitor(s, suggestions.brandId)}
                  disabled={addingComp === s.handle}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg btn-gold text-xs disabled:opacity-40">
                  {addingComp === s.handle ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                  Add
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Competitors */}
      <div>
        <h2 className="font-display text-xl font-semibold text-zinc-100 mb-1">Competitors</h2>
        <p className="section-label mb-4">// tracked competitors across brands</p>
        <CompetitorList brands={brands} brandMap={brandMap} />
      </div>
    </div>
  );
}


function CompetitorList({ brands, brandMap }) {
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [handle, setHandle] = useState("");
  const [platform, setPlatform] = useState("instagram");
  const [selBrands, setSelBrands] = useState([]);
  const [brandFilter, setBrandFilter] = useState("");
  const [analyzing, setAnalyzing] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const fetchComps = async () => {
    try {
      const params = {};
      if (brandFilter) params.brand = brandFilter;
      const res = await api.get("/competitors", { params });
      setCompetitors(res.data.competitors);
    } catch { toast.error("Failed to load competitors"); }
    setLoading(false);
  };
  useEffect(() => { fetchComps(); }, [brandFilter]);

  const toggleBrand = (bid) => setSelBrands((p) => p.includes(bid) ? p.filter((x) => x !== bid) : [...p, bid]);

  const handleAdd = async () => {
    if (!name || !handle) return;
    try {
      await api.post("/competitors", { name, handle, platform, brand_ids: selBrands });
      toast.success("Competitor added");
      setName(""); setHandle(""); setPlatform("instagram"); setSelBrands([]);
      fetchComps();
    } catch { toast.error("Failed to add"); }
  };

  const handleAnalyze = async (id) => {
    setAnalyzing(id);
    try {
      await api.post(`/competitors/${id}/analyze`);
      toast.success("Analysis complete");
      fetchComps();
      setExpanded(id);
    } catch { toast.error("Analysis failed"); }
    setAnalyzing(null);
  };

  const PlatformIcon = ({ p }) => {
    if (p === "youtube") return <Youtube size={14} className="text-[#c47272]" />;
    return <Instagram size={14} className="text-[#c47272]" />;
  };

  const daysAgo = (iso) => { if (!iso) return null; const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000); return d === 0 ? "today" : `${d}d ago`; };

  if (loading) return <div className="flex justify-center py-8"><div className="spinner-gold" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={brandFilter || "all"} onValueChange={(v) => setBrandFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[180px] bg-studio-surface border-[#2a2e36] text-zinc-300 text-sm h-10 rounded-xl">
            <SelectValue placeholder="All brands" />
          </SelectTrigger>
          <SelectContent className="bg-studio-raised border-[#2a2e36]">
            <SelectItem value="all" className="text-zinc-300 text-sm">All brands</SelectItem>
            {brands.map((b) => (
              <SelectItem key={b.id} value={b.id} className="text-zinc-300 text-sm">{b.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-zinc-500 font-mono">{competitors.length} tracked</span>
      </div>
      <div className="bg-studio-raised border border-[#2a2e36] rounded-xl p-4 space-y-3">
        <p className="section-label">// add competitor</p>
        <div className="flex gap-2 flex-wrap">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name"
            className="flex-1 min-w-[120px] h-10 px-3 bg-studio-surface border border-[#2a2e36] rounded-xl text-sm text-zinc-200 outline-none focus:border-gold-dim placeholder:text-zinc-600" />
          <input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="@handle"
            className="flex-1 min-w-[120px] h-10 px-3 bg-studio-surface border border-[#2a2e36] rounded-xl text-sm text-zinc-200 outline-none focus:border-gold-dim placeholder:text-zinc-600" />
          <div className="flex bg-studio-surface border border-[#2a2e36] rounded-xl overflow-hidden">
            {["instagram", "youtube"].map((p) => (
              <button key={p} onClick={() => setPlatform(p)} className={`px-3 py-2.5 text-xs ${platform === p ? "bg-gold/10 text-gold" : "text-zinc-500"}`}>
                {p === "instagram" ? "IG" : "YT"}
              </button>
            ))}
          </div>
          <button onClick={handleAdd} className="btn-gold px-4 h-10 rounded-xl text-sm flex items-center gap-1"><Plus size={14} /> Add</button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {brands.map((b) => (
            <button key={b.id} onClick={() => toggleBrand(b.id)}
              className={`text-[11px] px-2.5 py-1 rounded-full border ${selBrands.includes(b.id) ? "border-gold/40 text-gold bg-gold/5" : "border-[#2a2e36] text-zinc-500"}`}>
              <span className="w-1.5 h-1.5 rounded-full inline-block mr-1" style={{ background: b.color_hex }} />{b.short_name}
            </button>
          ))}
        </div>
      </div>
      {competitors.map((c) => (
        <div key={c.id} className="bg-studio-raised border border-[#2a2e36] rounded-xl overflow-hidden brand-card">
          <div className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <PlatformIcon p={c.platform} />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-zinc-200 font-medium">{c.name}</p>
                <p className="text-xs text-zinc-500 font-mono">{c.handle}</p>
              </div>
              <div className="flex flex-wrap gap-1">
                {(c.brand_ids || []).map((bid) => { const b = brandMap[bid]; return b ? <span key={bid} className="text-[9px] px-1.5 py-0.5 rounded-full border" style={{ borderColor: `${b.color_hex}40`, color: b.color_hex }}>{b.short_name}</span> : null; })}
              </div>
              <button onClick={() => api.delete(`/competitors/${c.id}`).then(fetchComps)} className="text-zinc-600 hover:text-status-red transition-colors p-1"><Trash2 size={13} /></button>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {[["followers", (c.follower_count||0).toLocaleString()], ["engagement", c.avg_engagement ? `${c.avg_engagement}%` : "--"], ["last post", c.last_post_date ? daysAgo(c.last_post_date) : "--"], ["analyzed", c.last_analyzed ? daysAgo(c.last_analyzed) : "never"]].map(([l,v]) => (
                <div key={l}><p className="text-[10px] text-zinc-500 uppercase font-mono tracking-wider">{l}</p><p className="stat-number text-sm text-zinc-200">{v}</p></div>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 px-4 py-3 border-t border-[#222630]">
            <button onClick={() => handleAnalyze(c.id)} disabled={analyzing === c.id}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#363b45] text-zinc-400 text-xs hover:text-gold hover:border-gold/30 transition-colors disabled:opacity-40">
              {analyzing === c.id ? <Loader2 size={12} className="animate-spin" /> : <BarChart3 size={12} />}
              {analyzing === c.id ? "Analyzing..." : "Analyze"}
            </button>
            {c.analysis && (
              <button onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#363b45] text-zinc-400 text-xs hover:text-gold transition-colors">
                <Eye size={12} /> {expanded === c.id ? "Hide" : "View"} Analysis
              </button>
            )}
          </div>
          {expanded === c.id && c.analysis && (
            <div className="px-4 pb-4"><div className="bg-studio-surface border border-[#2a2e36] rounded-xl p-4">
              <p className="section-label mb-2">// analysis</p>
              <p className="text-xs text-zinc-300 leading-relaxed whitespace-pre-wrap">{c.analysis}</p>
            </div></div>
          )}
        </div>
      ))}
    </div>
  );
}
